/* Fifth & Piedmont V8: the interaction layer. One small module, no dependencies.
   Owner rules: no zoom or scaling; vertical wheel never intercepted; motion off under
   prefers-reduced-motion; nothing is hidden when JavaScript is unavailable. */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');

/* Reveal ([data-v8-reveal]: headings rise 12px and fade in once) now runs in fp-v9.js's fpMotion scan,
   which also handles content added after load. */

/* Hero: crossfade slides with a progress line; pause button; pauses on hover and focus; swipe. */
class Hero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-goto]')];
    this.pauseBtn = this.querySelector('[data-pause]');
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 7000;
    if (this.slides.length < 2) return;
    this.paused = reduce.matches;
    this.held = false;
    this.style.setProperty('--v8-interval', `${this.interval}ms`);
    this.dots.forEach((d) => d.addEventListener('click', () => { this.go(Number(d.dataset.goto)); }));
    this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));
    this.addEventListener('mouseenter', () => this.hold(true));
    this.addEventListener('mouseleave', () => this.hold(false));
    this.addEventListener('focusin', () => this.hold(true));
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); });
    this.addEventListener('keydown', (e) => {
      if (e.target.closest('[role="tablist"]') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); this.dots[this.index]?.focus();
      }
    });
    let x0 = null;
    this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 50) { this.setPaused(true); this.go(this.index + (dx < 0 ? 1 : -1)); }
    });
    this.setPaused(this.paused);
  }
  go(i) {
    const n = this.slides.length;
    const next = (i + n) % n;
    if (next === this.index) return this.restart();
    this.slides.forEach((s, k) => {
      const on = k === next;
      s.classList.toggle('is-active', on);
      s.toggleAttribute('inert', !on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    this.dots.forEach((d, k) => d.setAttribute('aria-selected', k === next ? 'true' : 'false'));
    this.index = next;
    this.restart();
  }
  restart() {
    clearTimeout(this.timer);
    this.classList.remove('is-running'); void this.offsetWidth;
    if (this.paused || this.held) return;
    this.classList.add('is-running');
    this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
  }
  hold(on) { this.held = on; this.classList.toggle('is-held', on); this.restart(); }
  setPaused(on) {
    this.paused = on;
    this.classList.toggle('is-paused', on);
    if (this.pauseBtn) {
      this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.pauseBtn.setAttribute('aria-label', on ? 'Play slideshow' : 'Pause slideshow');
    }
    this.restart();
  }
}
if (!customElements.get('fp-v8-hero')) customElements.define('fp-v8-hero', Hero);

/* Tabs: filter one rail by product type; arrow keys move between tabs. */
class Tabs extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-tablist]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.items = [...this.querySelectorAll('[data-rail] > li[data-type]')];
    if (!this.list || this.tabs.length < 2) return;
    this.list.hidden = false;
    this.tabs.forEach((t, i) => {
      t.tabIndex = i === 0 ? 0 : -1;
      t.addEventListener('click', () => this.select(i));
      t.addEventListener('keydown', (e) => {
        const k = { ArrowRight: 1, ArrowLeft: -1, Home: -i, End: this.tabs.length - 1 - i }[e.key];
        if (k === undefined) return;
        e.preventDefault();
        const j = (i + k + this.tabs.length) % this.tabs.length;
        this.select(j); this.tabs[j].focus();
      });
    });
  }
  select(i) {
    const types = (this.tabs[i].dataset.types || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', k === i ? 'true' : 'false'); t.tabIndex = k === i ? 0 : -1; });
    for (const li of this.items) li.hidden = types.length > 0 && !types.includes(li.dataset.type);
    const rail = this.querySelector('[data-rail]');
    if (rail) rail.scrollLeft = 0;
    this.querySelector('fp-v6-rail')?.update?.();
  }
}
if (!customElements.get('fp-v8-tabs')) customElements.define('fp-v8-tabs', Tabs);

/* Marquee: CSS animation on a duplicated track; pause button; static under reduced motion. */
class Marquee extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-track]');
    if (!track || reduce.matches) return;
    const copy = track.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a').forEach((a) => { a.tabIndex = -1; });
    copy.removeAttribute('data-track');
    track.after(copy);
    this.classList.add('is-moving');
    const btn = this.querySelector('[data-pause]');
    btn?.addEventListener('click', () => {
      const on = !this.classList.contains('is-paused');
      this.classList.toggle('is-paused', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Play moving designer names' : 'Pause moving designer names');
    });
  }
}
if (!customElements.get('fp-v8-marquee')) customElements.define('fp-v8-marquee', Marquee);

/* Shoppable look: a hotspot highlights its card and opens Quick shop (or the product on phones). */
document.addEventListener('click', (e) => {
  const spot = e.target.closest('[data-v8-hotspot]');
  if (!spot) return;
  const scope = spot.closest('.v8-look');
  const card = scope?.querySelector(`[data-fp-handle="${spot.dataset.v8Hotspot}"]`);
  if (!card) return;
  e.preventDefault();
  const quick = card.querySelector('button.quick-add__button');
  if (quick && quick.offsetParent !== null) quick.click();
  else location.href = card.querySelector('a[href]')?.href || location.href;
});
document.addEventListener('pointerover', (e) => {
  const spot = e.target.closest('[data-v8-hotspot]');
  document.querySelectorAll('.v8-look [data-fp-handle].is-spot').forEach((c) => c.classList.remove('is-spot'));
  if (!spot) return;
  spot.closest('.v8-look')?.querySelector(`[data-fp-handle="${spot.dataset.v8Hotspot}"]`)?.classList.add('is-spot');
});

/* Header: a hairline appears once the page scrolls under the sticky header. */
(() => {
  // the theme may scroll the body rather than the window, so listen everywhere and read both
  const set = () => document.documentElement.classList.toggle('v8-scrolled', Math.max(scrollY, document.body.scrollTop, document.documentElement.scrollTop) > 8);
  set(); document.addEventListener('scroll', set, { passive: true, capture: true });
})();
